/**
 * Revenue Control Center Dashboard
 *
 * Single pane of glass for all growth, revenue, pricing, user activation, and funnel intelligence
 */

import { Card, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { RefreshCw } from 'lucide-react';
import {
  useRevenueControlCenter,
  useRefreshRevenueControlCenter,
} from '@/features/revenue-control-center';
import { RevenueOverview } from './sections/RevenueOverview';
import { FunnelPerformance } from './sections/FunnelPerformance';
import { ReliabilityRevenueImpact } from './sections/ReliabilityRevenueImpact';
import { PricingIntelligence } from './sections/PricingIntelligence';
import { SalesAutomationControl } from './sections/SalesAutomationControl';
import { MarketingAutomationInsights } from './sections/MarketingAutomationInsights';
import { CustomerSuccessHealth } from './sections/CustomerSuccessHealth';
import { BoardSummaries } from './sections/BoardSummaries';

export function RevenueControlCenter() {
  const { data, isLoading, error } = useRevenueControlCenter();
  const refreshMutation = useRefreshRevenueControlCenter();

  if (error) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>Error Loading Dashboard</CardTitle>
            <CardDescription>
              {error instanceof Error ? error.message : 'Failed to load revenue control center'}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">FORGE Revenue Control Center</h1>
          <p className="text-muted-foreground mt-2">
            Single pane of glass for all growth, revenue, pricing, user activation, and funnel intelligence
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refreshMutation.mutate()}
          disabled={refreshMutation.isPending}
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${refreshMutation.isPending ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="grid w-full grid-cols-8">
          <TabsTrigger value="overview">Revenue</TabsTrigger>
          <TabsTrigger value="funnel">Funnel</TabsTrigger>
          <TabsTrigger value="reliability">Reliability</TabsTrigger>
          <TabsTrigger value="pricing">Pricing</TabsTrigger>
          <TabsTrigger value="sales">Sales</TabsTrigger>
          <TabsTrigger value="marketing">Marketing</TabsTrigger>
          <TabsTrigger value="success">Success</TabsTrigger>
          <TabsTrigger value="board">Board</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6">
          <RevenueOverview data={data?.revenueOverview} />
        </TabsContent>

        <TabsContent value="funnel" className="mt-6">
          <FunnelPerformance data={data?.funnelPerformance} />
        </TabsContent>

        <TabsContent value="reliability" className="mt-6">
          <ReliabilityRevenueImpact data={data?.reliabilityRevenueImpact} />
        </TabsContent>

        <TabsContent value="pricing" className="mt-6">
          <PricingIntelligence data={data?.pricingIntelligence} />
        </TabsContent>

        <TabsContent value="sales" className="mt-6">
          <SalesAutomationControl data={data?.salesAutomationControl} />
        </TabsContent>

        <TabsContent value="marketing" className="mt-6">
          <MarketingAutomationInsights data={data?.marketingAutomationInsights} />
        </TabsContent>

        <TabsContent value="success" className="mt-6">
          <CustomerSuccessHealth data={data?.customerSuccessHealth} />
        </TabsContent>

        <TabsContent value="board" className="mt-6">
          <BoardSummaries data={data?.boardSummaries} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
